import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { RowSelection } from '@/components/ui/use-row-selection';

/**
 * Shared generic list-rendering component (Resolution 11): a thin wrapper over
 * `@tanstack/react-table`'s `useReactTable`/`getCoreRowModel`, rendered through the shadcn `Table`
 * primitives. Every admin list table (Tenants, Organizations, Users, the roster/role-assignments
 * table, entitlements) reuses this instead of a near-duplicate table implementation.
 */
export type DataTableSelection<TData> = RowSelection & {
  /** The row's stable identity — the same id the caller passed to `useRowSelection`. */
  readonly rowId: (row: TData) => string;
  /** A human name for the row, so its checkbox is not announced as just "checkbox". */
  readonly rowLabel: (row: TData) => string;
};

export function DataTable<TData, TValue>({
  columns,
  data,
  emptyMessage,
  selection,
}: {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  emptyMessage: string;
  /**
   * Opt-in multi-selection (§ List: "Multi-select via checkbox opens a bulk action bar").
   *
   * THE CHECKBOX COLUMN IS RENDERED HERE, NOT PASSED IN AS A `ColumnDef`, and that is a
   * correctness requirement rather than an arrangement preference. `flexRender` renders a column's
   * `cell` as a COMPONENT TYPE, so a new `columns` array identity remounts every cell in the
   * table — which is why the admin tables memoize `columns` and keep per-keystroke state out of
   * its dependency list (see `TenantOrganizationsPage`'s own note, and the uncontrolled row-rename
   * input that note exists to protect). Selection changes on every click; routing it through
   * `columns` would remount all cells on each one, and an in-progress inline edit would lose what
   * had been typed into it. Rendered as plain JSX in this component's own body, it re-renders
   * without touching the caller's memoized column identities at all.
   */
  selection?: DataTableSelection<TData>;
}) {
  const { t } = useTranslation('common');
  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });
  const spannedColumns = columns.length + (selection === undefined ? 0 : 1);

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {selection === undefined ? null : (
              <TableHead className="w-10">
                <Checkbox
                  checked={selection.allSelected}
                  indeterminate={selection.someSelected}
                  onChange={selection.toggleAll}
                  aria-label={t('selectAll')}
                />
              </TableHead>
            )}
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={spannedColumns} className="text-center text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          table.getRowModel().rows.map((row) => {
            const id = selection?.rowId(row.original);
            return (
              <TableRow
                key={row.id}
                {...(id !== undefined && selection?.isSelected(id) === true
                  ? { 'data-state': 'selected' as const }
                  : {})}
              >
                {selection === undefined || id === undefined ? null : (
                  <TableCell>
                    <Checkbox
                      checked={selection.isSelected(id)}
                      onChange={() => selection.toggle(id)}
                      aria-label={t('selectRow', { name: selection.rowLabel(row.original) })}
                    />
                  </TableCell>
                )}
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
